import { Badge, Button, Checkbox, HStack, Table, Tbody, Td, Text, Th, Thead, Tr } from "@chakra-ui/react";
import React from "react";
import { brandPalette } from "../../../constants/brandPalette";
import {
  TIMING_LEVELS,
  formatSubmittedAt,
  getSubmittedAt,
  getSubmissionTimingLevel,
  isGraded,
} from "./submission-timing";

const STUDENT_PDF_BASE = "https://api.edutopiaonline.com/public/student_pdfs/";

function openStudentPdf(filename) {
  if (!filename) return;
  window.open(`${STUDENT_PDF_BASE}${filename}`, "_blank", "noopener,noreferrer");
}

function TimingBadge({ level }) {
  const meta = TIMING_LEVELS[level];
  if (!meta) return null;
  return (
    <Badge
      px={2}
      py={0.5}
      borderRadius="md"
      fontWeight="700"
      fontSize="sm"
      bg={meta.bg}
      color={meta.color}
      title={meta.label}
    >
      {meta.face} {meta.label}
    </Badge>
  );
}

const SubmissionsTable = ({
  rows,
  isLoading,
  onCorrect,
  onPreviewCorrection,
  onRefuse,
  refusingId = null,
  filtersActive,
  selectedIds,
  onToggleSelect,
  maxSelected = 5,
}) => {
  const submissions = rows || [];
  const selectionEnabled = typeof onToggleSelect === "function";
  const selectedSet = selectedIds instanceof Set ? selectedIds : new Set();

  if (isLoading) {
    return (
      <Text fontSize="sm" color="var(--text-muted)" py={8} textAlign="center">
        Loading submissions…
      </Text>
    );
  }

  if (!submissions.length) {
    return (
      <Text fontSize="sm" color="var(--text-muted)" py={8} textAlign="center">
        {filtersActive
          ? "No submissions match the current filters."
          : "No PDF submissions for this exam yet."}
      </Text>
    );
  }

  return (
    <Table variant="simple" size="sm">
      <Thead>
        <Tr>
          {selectionEnabled ? <Th color="var(--text-muted)" w="40px" /> : null}
          <Th color="var(--text-muted)">Student</Th>
          <Th color="var(--text-muted)">Submitted at</Th>
          <Th color="var(--text-muted)">Timing</Th>
          <Th color="var(--text-muted)">Status</Th>
          <Th color="var(--text-muted)">Score</Th>
          <Th color="var(--text-muted)">Assistant</Th>
          <Th color="var(--text-muted)">PDF</Th>
          <Th color="var(--text-muted)">Actions</Th>
        </Tr>
      </Thead>
      <Tbody>
        {submissions.map((row) => {
          const graded = isGraded(row);
          const studentName = row?.student?.name || "—";
          const userName = row?.student?.user_name;
          const assistantName = row?.assistant?.name;
          const submittedAt = getSubmittedAt(row);
          const timingLevel = getSubmissionTimingLevel(row);
          const isSelected = selectedSet.has(row.id);
          const selectionDisabled = !isSelected && selectedSet.size >= maxSelected;
          return (
            <Tr key={row.id} bg={isSelected ? "rgba(91, 13, 22, 0.06)" : undefined}>
              {selectionEnabled ? (
                <Td>
                  <Checkbox
                    isChecked={isSelected}
                    isDisabled={selectionDisabled}
                    onChange={() => onToggleSelect(row)}
                    colorScheme="red"
                    borderColor="var(--border-subtle)"
                    title={
                      selectionDisabled
                        ? `You can select up to ${maxSelected} submissions`
                        : "Select for bulk AI grading"
                    }
                  />
                </Td>
              ) : null}
              <Td>
                <Text fontWeight="700" color="var(--text-primary)">
                  {studentName}
                </Text>
                {userName ? (
                  <Text fontSize="xs" color="var(--text-muted)">
                    {userName}
                  </Text>
                ) : null}
              </Td>
              <Td>
                <Text fontSize="sm" fontWeight="600" color="var(--text-primary)" whiteSpace="nowrap">
                  {formatSubmittedAt(submittedAt)}
                </Text>
              </Td>
              <Td>
                {!graded && timingLevel ? <TimingBadge level={timingLevel} /> : "—"}
              </Td>
              <Td>
                <Badge
                  px={2}
                  py={0.5}
                  borderRadius="md"
                  fontWeight="700"
                  fontSize="xs"
                  bg={graded ? "rgba(72, 187, 120, 0.15)" : "rgba(237, 137, 54, 0.15)"}
                  color={graded ? "green.700" : "orange.700"}
                >
                  {graded ? "Graded" : "Pending"}
                </Badge>
              </Td>
              <Td>
                <Text fontSize="sm" fontWeight="600" color="var(--text-primary)">
                  {graded ? row.right_answers : "—"}
                </Text>
              </Td>
              <Td>
                <Text fontSize="sm" fontWeight="600" color="var(--text-primary)">
                  {assistantName || "—"}
                </Text>
              </Td>
              <Td>
                <Button
                  size="sm"
                  variant="outline"
                  borderColor="var(--border-subtle)"
                  color={brandPalette.primary}
                  fontWeight="700"
                  onClick={() => openStudentPdf(row.student_pdf)}
                >
                  View PDF
                </Button>
              </Td>
              <Td>
                <HStack spacing={2} flexWrap="wrap">
                  {graded ? (
                    <Button
                      size="sm"
                      variant="outline"
                      borderColor="var(--border-subtle)"
                      color={brandPalette.primary}
                      fontWeight="700"
                      onClick={() => onPreviewCorrection(row)}
                    >
                      View correction
                    </Button>
                  ) : (
                    <>
                      <Button
                        size="sm"
                        bg={brandPalette.primary}
                        color="white"
                        fontWeight="700"
                        _hover={{ bg: brandPalette.secondary }}
                        onClick={() => onCorrect(row)}
                      >
                        Correct
                      </Button>
                      {typeof onRefuse === "function" ? (
                        <Button
                          size="sm"
                          variant="outline"
                          borderColor="red.200"
                          color="red.600"
                          fontWeight="700"
                          isLoading={refusingId === row.id}
                          onClick={() => onRefuse(row)}
                        >
                          Refuse
                        </Button>
                      ) : null}
                    </>
                  )}
                </HStack>
              </Td>
            </Tr>
          );
        })}
      </Tbody>
    </Table>
  );
};

export default SubmissionsTable;
